/**
 * ============================================================
 * KEJJA OS — src/pages/library/BrowsePage.tsx
 * Folders + items for the current scope.
 * ============================================================
 */

import { useState } from 'react';
import {
  Folder, FolderPlus, Star, FileText, IdCard,
  Receipt, Stethoscope, GraduationCap, Award, FileSignature,
  Image as ImageIcon, Link as LinkIcon, FileQuestion, X,
} from 'lucide-react';
import { useLibraryContext } from './LibraryLayout';
import {
  useFolders, useItems, useTags,
  useCreateFolder, useDeleteFolder,
} from '@/hooks/useLibrary';
import type { LibraryDocType, LibraryItem } from '@/lib/libraryApi';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

const DOC_TYPE_ICON: Record<LibraryDocType, React.ComponentType<{ className?: string }>> = {
  note: FileText,
  id_document: IdCard,
  receipt: Receipt,
  medical: Stethoscope,
  school: GraduationCap,
  certificate: Award,
  contract: FileSignature,
  photo: ImageIcon,
  link: LinkIcon,
  other: FileQuestion,
};

const DOC_TYPE_LABEL: Record<LibraryDocType, string> = {
  note: 'Note',
  id_document: 'ID',
  receipt: 'Receipt',
  medical: 'Medical',
  school: 'School',
  certificate: 'Certificate',
  contract: 'Contract',
  photo: 'Photo',
  link: 'Link',
  other: 'Other',
};

export default function BrowsePage() {
  const {
    homeId, scope, searchQuery, favoritesOnly, openNewItem, openEditItem,
  } = useLibraryContext();

  const [selectedFolderId, setSelectedFolderId] = useState<string | null | undefined>(undefined);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');

  const { data: foldersData, isLoading: foldersLoading } = useFolders(homeId, scope);
  const { data: itemsData, isLoading: itemsLoading } = useItems(homeId, {
    scope,
    folderId: selectedFolderId,
    q: searchQuery || undefined,
    favoritesOnly: favoritesOnly || undefined,
    tags: selectedTags.length > 0 ? selectedTags : undefined,
  });
  const { data: tagsData } = useTags(homeId);
  const createFolderMut = useCreateFolder(homeId);
  const deleteFolderMut = useDeleteFolder(homeId);

  const folders = foldersData?.folders ?? [];
  const items = itemsData?.items ?? [];
  const tags = tagsData?.tags ?? [];

  const handleCreateFolder = () => {
    if (!newFolderName.trim()) return;
    createFolderMut.mutate({
      name: newFolderName.trim(),
      ownerUserId: scope === 'personal' ? undefined : null,
    }, {
      onSuccess: () => { setNewFolderName(''); setShowNewFolder(false); },
    });
  };

  const handleDeleteFolder = (f: (typeof folders)[number]) => {
    if (!confirm(`Delete folder "${f.name}"? Items inside will be moved to "No folder".`)) return;
    deleteFolderMut.mutate(f.id);
    if (selectedFolderId === f.id) setSelectedFolderId(undefined);
  };

  const toggleTag = (tag: string) => {
    setSelectedTags(selectedTags.includes(tag)
      ? selectedTags.filter((t) => t !== tag)
      : [...selectedTags, tag]);
  };

  return (
    <div className="space-y-5 pb-12">
      {/* Folder bar */}
      <div className="flex items-center gap-1.5 flex-wrap">
        <button
          onClick={() => setSelectedFolderId(undefined)}
          className={cn(
            'flex items-center gap-1 px-2.5 py-1 rounded-full border text-[11px] font-medium transition-all',
            selectedFolderId === undefined
              ? 'border-purple-500/60 bg-purple-500/10 text-purple-700 dark:text-purple-300'
              : 'border-border text-muted-foreground',
          )}
        >
          All
        </button>
        <button
          onClick={() => setSelectedFolderId(null)}
          className={cn(
            'flex items-center gap-1 px-2.5 py-1 rounded-full border text-[11px] font-medium transition-all',
            selectedFolderId === null
              ? 'border-purple-500/60 bg-purple-500/10 text-purple-700 dark:text-purple-300'
              : 'border-border text-muted-foreground',
          )}
        >
          No folder
        </button>
        {folders.map((f) => {
          const on = selectedFolderId === f.id;
          return (
            <div key={f.id} className="relative group">
              <button
                onClick={() => setSelectedFolderId(f.id)}
                className={cn(
                  'flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-medium transition-all',
                  on ? 'border-transparent' : 'border-border text-muted-foreground',
                )}
                style={on ? {
                  backgroundColor: `${f.color}22`,
                  color: f.color,
                  borderColor: `${f.color}66`,
                } : undefined}
              >
                {f.icon ?? <Folder className="w-3 h-3" />}
                <span className="truncate max-w-[120px]">{f.name}</span>
              </button>
              <button
                onClick={() => handleDeleteFolder(f)}
                className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-rose-600 text-white items-center justify-center hidden group-hover:flex"
                title="Delete folder"
              >
                <X className="w-2.5 h-2.5" />
              </button>
            </div>
          );
        })}
        <Button
          size="sm" variant="ghost" className="h-6 text-[10px] text-muted-foreground"
          onClick={() => setShowNewFolder(true)}
        >
          <FolderPlus className="w-3 h-3 mr-0.5" /> New folder
        </Button>
      </div>

      {showNewFolder && (
        <Card className="border-purple-300 dark:border-purple-900/50 bg-purple-50/30 dark:bg-purple-950/10">
          <CardContent className="p-3 flex items-center gap-2">
            <Input
              autoFocus
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()}
              placeholder="Folder name"
              className="flex-1 h-8 text-xs"
            />
            <Button
              size="sm"
              onClick={handleCreateFolder}
              disabled={!newFolderName.trim() || createFolderMut.isPending}
              className="bg-purple-600 hover:bg-purple-700 text-white h-8 text-xs"
            >
              Add
            </Button>
            <Button
              size="sm" variant="ghost"
              onClick={() => { setShowNewFolder(false); setNewFolderName(''); }}
              className="h-8"
            >
              <X className="w-3.5 h-3.5" />
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Tag chips */}
      {tags.length > 0 && (
        <div className="flex items-center gap-1 flex-wrap">
          <span className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold mr-1">Tags</span>
          {tags.slice(0, 15).map((t) => {
            const on = selectedTags.includes(t.tag);
            return (
              <button
                key={t.tag}
                onClick={() => toggleTag(t.tag)}
                className={cn(
                  'text-[10px] px-2 py-0.5 rounded-full border transition-colors',
                  on
                    ? 'bg-purple-600 text-white border-transparent'
                    : 'border-border text-muted-foreground hover:bg-muted',
                )}
              >
                #{t.tag} <span className="opacity-60">{t.count}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Items */}
      {itemsLoading || foldersLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-16" /><Skeleton className="h-16" /><Skeleton className="h-16" />
        </div>
      ) : items.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-8 text-center">
            <FileText className="w-10 h-10 text-muted-foreground/40 mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">
              {searchQuery || selectedTags.length > 0 || favoritesOnly
                ? 'Nothing matches. Try different filters.'
                : `No ${scope} items yet.`}
            </p>
            <Button
              size="sm" className="mt-3 bg-purple-600 hover:bg-purple-700 text-white"
              onClick={() => openNewItem({
                folderId: selectedFolderId !== undefined ? selectedFolderId : null,
                ownerScope: scope,
              })}
            >
              Add first item
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {items.map((item) => (
            <ItemCard key={item.id} item={item} onClick={() => openEditItem(item.id)} />
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================
// ItemCard
// ============================================================

function ItemCard({ item, onClick }: { item: LibraryItem; onClick: () => void }) {
  const Icon = DOC_TYPE_ICON[item.docType];

  return (
    <Card className="cursor-pointer hover:ring-1 hover:ring-purple-500/30 transition-all" onClick={onClick}>
      <CardContent className="p-3">
        <div className="flex items-start gap-3">
          <div className="h-8 w-8 rounded-lg bg-purple-100 dark:bg-purple-950/40 text-purple-700 dark:text-purple-300 flex items-center justify-center flex-shrink-0">
            <Icon className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start gap-2">
              <span className="font-semibold text-sm truncate flex-1">{item.title}</span>
              {item.isFavorite && <Star className="w-3 h-3 fill-rose-500 text-rose-500 flex-shrink-0 mt-0.5" />}
            </div>
            <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
              <Badge variant="outline" className="text-[9px] h-4 px-1 font-normal">
                {DOC_TYPE_LABEL[item.docType]}
              </Badge>
              {(item.tags ?? []).slice(0, 3).map((t) => (
                <Badge key={t} variant="outline" className="text-[9px] h-4 px-1 font-normal text-purple-700 dark:text-purple-300 border-purple-300">
                  #{t}
                </Badge>
              ))}
            </div>
            {item.body && (
              <div className="text-[11px] text-muted-foreground mt-1 line-clamp-2">{item.body}</div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
